import { createRouter, createWebHashHistory } from 'vue-router'
import type {
  Router,
  RouteLocationNormalized,
  RouteRecordNormalized,
  RouteRecordRaw
} from 'vue-router'
import { isUrl } from '@/utils/is'
import { omit, cloneDeep } from 'lodash-es'

const MenuEmpty = () => import('@/views/Pages/Common/MenuEmpty.vue')
const modules = import.meta.glob('../views/**/*.{vue,tsx}')

/* Layout */
export const Layout = () => import('@/layout/Layout.vue')

export const getParentLayout = () => {
  return () =>
    new Promise((resolve) => {
      resolve({
        name: 'ParentLayout'
      })
    })
}

export const getRawRoute = (route: RouteLocationNormalized): RouteLocationNormalized => {
  if (!route) return route
  const { matched, ...opt } = route
  return {
    ...opt,
    matched: (matched
      ? matched.map((item) => ({
          meta: item.meta,
          name: item.name,
          path: item.path
        }))
      : undefined) as RouteRecordNormalized[]
  }
}

const findRedirectChild = (
  children: AppRouteRecordRaw[],
  redirect: string | undefined,
  currentPath: string
) => {
  if (!redirect) return null
  return (
    children.find((child) => {
      const childFullPath = pathResolve(currentPath, child.path)
      return childFullPath === redirect
    }) || null
  )
}

// 前端控制路由生成
export const generateRoutesByFrontEnd = (
  routes: AppRouteRecordRaw[],
  keys: string[],
  basePath = '/'
): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  const normalizePath = (path?: string) => {
    return String(path || '')
      .replace(/\/+/g, '/')
      .trim()
  }

  const getVisibleChildren = (children: AppRouteRecordRaw[] = []) => {
    return children.filter((item) => !item.meta?.hidden)
  }

  const isRouteMatched = (
    route: AppRouteRecordRaw,
    currentPath: string,
    onlyOneChild: Nullable<string>
  ) => {
    const meta = route.meta ?? {}

    for (const item of keys) {
      const key = String(item ?? '').trim()
      if (!key) continue

      if (isUrl(key)) {
        if (currentPath === key || route.path === key || onlyOneChild === key) {
          return true
        }
      } else {
        if (currentPath === key || route.path === key || meta.followRoute === key) {
          return true
        }

        // hidden 页面通过 activeMenu 跟随主菜单权限
        if (meta.hidden && meta.activeMenu && String(meta.activeMenu).trim() === key) {
          return true
        }

        if (onlyOneChild && onlyOneChild === key) {
          return true
        }
      }
    }

    return false
  }

  // 关键修复：
  // 1. 显式声明 hasOwnPage 的，仍按原逻辑处理
  // 2. 没有 children、但自己有 component 的叶子路由，也视为真正页面
  const hasOwnPage = (route: AppRouteRecordRaw) => {
    const meta = route.meta ?? {}

    if ((meta as Recordable).hasOwnPage === true) {
      return true
    }

    const hasChildren = Array.isArray(route.children) && route.children.length > 0
    if (!hasChildren && !!route.component) {
      return true
    }

    return false
  }

  const createEmptyChildRoute = (
    route: AppRouteRecordRaw,
    currentPath: string
  ): AppRouteRecordRaw => {
    return {
      path: '__empty__',
      name: `${String(route.name || currentPath).replace(/\//g, '_')}_empty`,
      component: MenuEmpty,
      meta: {
        title: `${String(route.meta?.title || '空页面')}`,
        hidden: true,
        noTagsView: true,
        activeMenu: currentPath
      }
    } as AppRouteRecordRaw
  }

  const findRedirectChild = (
    route: AppRouteRecordRaw,
    currentPath: string
  ): AppRouteRecordRaw | null => {
    if (!route.redirect || !route.children || route.children.length === 0) {
      return null
    }

    const redirectPath = normalizePath(String(route.redirect))
    const matchedChild = route.children.find((child) => {
      const childFullPath = normalizePath(pathResolve(currentPath, String(child.path || '')))
      return childFullPath === redirectPath
    })

    return matchedChild ? cloneDeep(matchedChild) : null
  }

  const mergeHiddenChildren = (
    originalChildren: AppRouteRecordRaw[] = [],
    filteredChildren: AppRouteRecordRaw[] = []
  ) => {
    const result = [...filteredChildren]
    const existedNames = new Set(result.map((item) => String(item.name || item.path || '')))

    originalChildren.forEach((child) => {
      if (!child.meta?.hidden) return

      const uniqueKey = String(child.name || child.path || '')
      if (!existedNames.has(uniqueKey)) {
        result.push(cloneDeep(child))
        existedNames.add(uniqueKey)
      }
    })

    return result
  }

  const rebuildRedirect = (route: AppRouteRecordRaw, currentPath: string) => {
    if (!route.children || route.children.length === 0) {
      delete route.redirect
      return
    }

    // 优先找非隐藏子路由作为默认跳转
    const firstVisibleChild = route.children.find((item) => !item.meta?.hidden)
    if (firstVisibleChild) {
      route.redirect = pathResolve(currentPath, String(firstVisibleChild.path || ''))
      return
    }

    // 如果全是隐藏页，就删掉 redirect，避免跳到异常地址
    delete route.redirect
  }

  for (const route of routes) {
    const meta = route.meta ?? {}
    const currentPath = pathResolve(basePath, route.path).trim()

    let onlyOneChild: Nullable<string> = null
    if (route.children && route.children.length === 1 && !meta.alwaysShow) {
      onlyOneChild = (
        isUrl(route.children[0].path)
          ? route.children[0].path
          : pathResolve(currentPath, route.children[0].path)
      ) as string
    }

    // 先递归 children
    const filteredChildren = route.children
      ? generateRoutesByFrontEnd(route.children, keys, currentPath)
      : []

    const visibleChildren = getVisibleChildren(filteredChildren)

    // 再判断自己是否命中
    const selfMatched = isRouteMatched(route, currentPath, onlyOneChild)

    // 自己没命中，子级也没命中：不要
    if (!selfMatched && filteredChildren.length === 0) {
      continue
    }

    const data = Object.assign({}, route) as AppRouteRecordRaw

    // 场景1：自己没命中，但子级命中了 -> 保留父级容器
    if (!selfMatched && filteredChildren.length > 0) {
      data.children = mergeHiddenChildren(route.children || [], filteredChildren)
      rebuildRedirect(data, currentPath)
      res.push(data)
      continue
    }

    // 场景2：自己命中了，且至少还有一个“可见子路由”
    if (selfMatched && visibleChildren.length > 0) {
      data.children = mergeHiddenChildren(route.children || [], filteredChildren)
      rebuildRedirect(data, currentPath)
      res.push(data)
      continue
    }

    // 场景3：自己命中了，但没有任何“可见子路由”
    if (selfMatched && visibleChildren.length === 0) {
      const routeMeta = data.meta ?? {}
      const allowRedirectFallback = (routeMeta as Recordable).fallbackToRedirectChild === true

      // 3.1 如果允许 fallback，则补 redirect 对应子页
      if (allowRedirectFallback) {
        const redirectChild = findRedirectChild(route, currentPath)
        if (redirectChild) {
          data.children = mergeHiddenChildren(route.children || [], [redirectChild])
          rebuildRedirect(data, currentPath)
          res.push(data)
          continue
        }
      }

      // 3.2 父级本身就是页面，直接显示自己
      if (hasOwnPage(route)) {
        data.children = route.children
          ? mergeHiddenChildren(route.children || [], filteredChildren)
          : undefined
        delete data.redirect
        res.push(data)
        continue
      }

      // 3.3 父级不是页面，也没有可见子页：挂空页面占位
      const emptyChild = createEmptyChildRoute(data, currentPath)
      data.children = mergeHiddenChildren(route.children || [], [...filteredChildren, emptyChild])
      data.redirect = pathResolve(currentPath, emptyChild.path)
      res.push(data)
      continue
    }
  }

  return res
}

// 后端控制路由生成
export const generateRoutesByServer = (routes: AppCustomRouteRecordRaw[]): AppRouteRecordRaw[] => {
  const res: AppRouteRecordRaw[] = []

  for (const route of routes) {
    const data: AppRouteRecordRaw = {
      path: route.path,
      name: route.name,
      redirect: route.redirect,
      meta: route.meta
    }
    if (route.component) {
      const comModule = modules[`../${route.component}.vue`] || modules[`../${route.component}.tsx`]
      const component = route.component as string
      if (!comModule && !component.includes('#')) {
        console.error(`未找到${route.component}.vue文件或${route.component}.tsx文件，`)
      } else {
        data.component =
          component === '#' ? Layout : component.includes('##') ? getParentLayout() : comModule
      }
    }
    if (route.children) {
      data.children = generateRoutesByServer(route.children)
    }
    res.push(data as AppRouteRecordRaw)
  }
  return res
}

export const pathResolve = (parentPath: string, path: string) => {
  if (isUrl(path)) return path
  const childPath = path.startsWith('/') || !path ? path : `/${path}`
  return `${parentPath}${childPath}`.replace(/\/\//g, '/').trim()
}

// 路由降级
export const flatMultiLevelRoutes = (routes: AppRouteRecordRaw[]) => {
  const modules: AppRouteRecordRaw[] = cloneDeep(routes)
  for (let index = 0; index < modules.length; index++) {
    const route = modules[index]
    if (!isMultipleRoute(route)) {
      continue
    }
    promoteRouteLevel(route)
  }
  return modules
}

// 层级是否大于2
const isMultipleRoute = (route: AppRouteRecordRaw) => {
  if (!route || !Reflect.has(route, 'children') || !route.children?.length) {
    return false
  }

  const children = route.children

  let flag = false
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    if (child.children?.length) {
      flag = true
      break
    }
  }
  return flag
}

// 生成二级路由
const promoteRouteLevel = (route: AppRouteRecordRaw) => {
  let router: Router | null = createRouter({
    routes: [route as RouteRecordRaw],
    history: createWebHashHistory()
  })

  const routes = router.getRoutes()
  addToChildren(routes, route.children || [], route)
  router = null

  route.children = route.children?.map((item) => omit(item, 'children'))
}

// 添加所有子菜单
const addToChildren = (
  routes: RouteRecordNormalized[],
  children: AppRouteRecordRaw[],
  routeModule: AppRouteRecordRaw
) => {
  for (let index = 0; index < children.length; index++) {
    const child = children[index]
    const route = routes.find((item) => item.name === child.name)
    if (!route) {
      continue
    }
    routeModule.children = routeModule.children || []
    if (!routeModule.children.find((item) => item.name === route.name)) {
      routeModule.children?.push(route as unknown as AppRouteRecordRaw)
    }
    if (child.children?.length) {
      addToChildren(routes, child.children, routeModule)
    }
  }
}
